import { Intervention, InterventionType } from '@/app/components/action-plan/types';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import Loading from '@/components/loading/loading';
import TabItem from '@/components/tab-item/tab-item';
import { Divider, Flex, Tabs, Text } from '@cmpsr/components';
import Card from './intervention-card';
import { getInterventionRiskData, sortInterventions } from '@/utils';

type InterventionsTabsProps = {
  selectedItems: string[];
  interventions: Intervention[];
  onItemSelectionChange: (id: string, checked: boolean) => void;
  isMaxSelectionReached: (id: string) => boolean;
  isLoading: boolean;
};

export default function InterventionTabs({
  selectedItems,
  interventions = [],
  onItemSelectionChange,
  isMaxSelectionReached,
  isLoading,
}: InterventionsTabsProps) {
  const filterAndSortInterventions = (type: string): Intervention[] => {
    if (type === InterventionType.ALL) {
      return interventions.map(getInterventionRiskData).sort(sortInterventions);
    }

    return interventions
      .filter(({ interventionType }) => interventionType === type)
      .map(getInterventionRiskData)
      .sort(sortInterventions);
  };

  return isLoading ? (
    <Loading position="fixed" top="0" left="0" />
  ) : (
    <Tabs isFitted isLazy variant="line" w="full" pt="spacer-3">
      <Tabs.List>
        {Object.values(InterventionType).map((category) => (
          <TabItem key={category}>{category}</TabItem>
        ))}
      </Tabs.List>
      <Divider />
      <Tabs.Panels maxH={'calc(100vh - 370px)'} pb="spacer-10" overflow="auto" pr="spacer-2">
        {Object.values(InterventionType).map((category) => (
          <Tabs.Panel key={category} px={0} mb="spacer-10">
            {filterAndSortInterventions(category).map((intervention) => (
              <Card
                key={intervention.id}
                selectedItems={selectedItems}
                onItemSelectionChange={onItemSelectionChange}
                isMaxSelectionReached={isMaxSelectionReached}
                intervention={intervention}
              />
            ))}
          </Tabs.Panel>
        ))}
        {interventions.length === 0 && (
          <Flex w="full" h="full" justifyContent="center" alignItems="center" direction="column" p="spacer-8">
            <IconCatch
              icon="congrats-04"
              alt="cancer type icon"
              size="l"
              height="70px"
              w="70px"
              borderRadius="radii-2"
              bgColor="none"
            />
            <Text variant="text-header-L" textAlign="center" pt="spacer-5" pb="spacer-2">
              Congratulations! You've{' '}
              <Text as="span" textStyle="text-header-l-v">
                minimized
              </Text>{' '}
              your cancer risk
            </Text>
            <Text variant="text-body-regular" color="text-secondary">
              Check back for additional actions as your risk profile evolves
            </Text>
          </Flex>
        )}
      </Tabs.Panels>
    </Tabs>
  );
}
